export const TICKET_STATUS = {
  OPEN: "open",
  IN_PROGRESS: "in_progress",
  RESOLVED: "resolved",
} as const;

export const TICKET_PRIORITY = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  CRITICAL: "critical",
} as const;

export const TICKET_USER_TYPE = {
  CLIENT: "client",
  BUSINESS: "business",
  GUEST: "guest",
} as const;

export const TICKET_CATEGORY = {
  ACCOUNT_ACCESS: "Account Access",
  BILLING: "Billing",
  BOOKING_ISSUE: "Booking Issue",
  REFUND_REQUEST: "Refund Request",
  SERVICE_COMPLAINT: "Service Complaint",
  TECHNICAL_ISSUE: "Technical Issue",
  OTHER: "Other",
} as const;

export const TICKET_CATEGORY_OPTIONS = [
  { value: "", label: "Select Category" },
  { value: TICKET_CATEGORY.ACCOUNT_ACCESS, label: "Account Access" },
  { value: TICKET_CATEGORY.BILLING, label: "Billing" },
  { value: TICKET_CATEGORY.BOOKING_ISSUE, label: "Booking Issue" },
  { value: TICKET_CATEGORY.REFUND_REQUEST, label: "Refund Request" },
  { value: TICKET_CATEGORY.SERVICE_COMPLAINT, label: "Service Complaint" },
  { value: TICKET_CATEGORY.TECHNICAL_ISSUE, label: "Technical Issue" },
  { value: TICKET_CATEGORY.OTHER, label: "Other" },
];

export const TICKET_CATEGORY_FORM_OPTIONS = TICKET_CATEGORY_OPTIONS.filter(
  (option) => option.value !== ""
);

export const TICKET_STATUS_OPTIONS = [
  { value: "", label: "All Status" },
  { value: TICKET_STATUS.OPEN, label: "Open" },
  { value: TICKET_STATUS.IN_PROGRESS, label: "In Progress" },
  { value: TICKET_STATUS.RESOLVED, label: "Resolved" },
];

export const TICKET_STATUS_FORM_OPTIONS = [
  { value: TICKET_STATUS.OPEN, label: "Open" },
  { value: TICKET_STATUS.IN_PROGRESS, label: "In Progress" },
  { value: TICKET_STATUS.RESOLVED, label: "Resolved" },
];

export const TICKET_USER_TYPE_OPTIONS = [
  { value: "", label: "All User Types" },
  { value: TICKET_USER_TYPE.CLIENT, label: "Client" },
  { value: TICKET_USER_TYPE.BUSINESS, label: "Business" },
  { value: TICKET_USER_TYPE.GUEST, label: "Guest" },
];

export const TICKET_PRIORITY_OPTIONS = [
  { value: "", label: "All Priorities" },
  { value: TICKET_PRIORITY.LOW, label: "Low" },
  { value: TICKET_PRIORITY.MEDIUM, label: "Medium" },
  { value: TICKET_PRIORITY.HIGH, label: "High" },
  { value: TICKET_PRIORITY.CRITICAL, label: "Critical" },
];

export const TICKET_STATUS_BADGE: Record<string, string> = {
  [TICKET_STATUS.OPEN]: "light-info",
  [TICKET_STATUS.IN_PROGRESS]: "light-warning",
  [TICKET_STATUS.RESOLVED]: "light-success",
};

export const TICKET_STATUS_LABEL: Record<string, string> = {
  [TICKET_STATUS.OPEN]: "Open",
  [TICKET_STATUS.IN_PROGRESS]: "In Progress",
  [TICKET_STATUS.RESOLVED]: "Resolved",
};

export const TICKET_PRIORITY_BADGE: Record<string, string> = {
  [TICKET_PRIORITY.LOW]: "light-secondary",
  [TICKET_PRIORITY.MEDIUM]: "light-info",
  [TICKET_PRIORITY.HIGH]: "light-warning",
  [TICKET_PRIORITY.CRITICAL]: "light-danger",
};

export const isResolvedTicket = (status: string) =>
  normalizeTicketStatus(status) === TICKET_STATUS.RESOLVED;

export const normalizeTicketStatus = (status?: string | null) =>
  status?.toLowerCase() ?? TICKET_STATUS.OPEN;

export const normalizeTicketPriority = (priority?: string | null) =>
  priority?.toLowerCase() ?? "";

export const formatTicketStatus = (status?: string | null) =>
  TICKET_STATUS_LABEL[normalizeTicketStatus(status)] ?? status ?? "";

export const getTicketStatusBadgeColor = (status?: string | null) =>
  TICKET_STATUS_BADGE[normalizeTicketStatus(status)] ?? "light-primary";

export const getTicketPriorityBadgeColor = (priority?: string | null) =>
  TICKET_PRIORITY_BADGE[normalizeTicketPriority(priority)] ?? "light-secondary";

export const TICKET_ENTITY_TYPE = {
  ORDER_ITEM: "order_item",
  BUSINESS: "business",
  PRODUCT: "product",
  BOOKING: "booking",
  ORDER: "order",
  TRANSACTION: "transaction",
  REFUND: "refund",
  RETURN: "return",
  USER: "user",
} as const;

export const TICKET_ENTITY_TYPE_META: Record<
  string,
  { label: string; badgeColor: string }
> = {
  [TICKET_ENTITY_TYPE.ORDER_ITEM]: { label: "Order Item", badgeColor: "light-warning" },
  [TICKET_ENTITY_TYPE.BUSINESS]: { label: "Business", badgeColor: "light-primary" },
  [TICKET_ENTITY_TYPE.PRODUCT]: { label: "Product", badgeColor: "light-success" },
  [TICKET_ENTITY_TYPE.BOOKING]: { label: "Booking", badgeColor: "light-info" },
  [TICKET_ENTITY_TYPE.ORDER]: { label: "Order", badgeColor: "light-warning" },
  [TICKET_ENTITY_TYPE.TRANSACTION]: { label: "Transaction", badgeColor: "light-warning" },
  [TICKET_ENTITY_TYPE.REFUND]: { label: "Refund", badgeColor: "light-danger" },
  [TICKET_ENTITY_TYPE.RETURN]: { label: "Return", badgeColor: "light-danger" },
  [TICKET_ENTITY_TYPE.USER]: { label: "User", badgeColor: "light-secondary" },
};

export const getTicketEntityLabel = (entityType?: string | null) =>
  TICKET_ENTITY_TYPE_META[String(entityType || "").toLowerCase()]?.label ||
  entityType ||
  "—";

export const getTicketEntityBadgeColor = (entityType?: string | null) =>
  TICKET_ENTITY_TYPE_META[String(entityType || "").toLowerCase()]?.badgeColor ||
  "light-secondary";

export const getTicketEntityDetailPath = (
  entityType?: string | null,
  entityId?: string | number | null,
) => {
  if (entityId == null || entityId === "") {
    return null;
  }

  const id = String(entityId);
  switch (String(entityType || "").toLowerCase()) {
    case TICKET_ENTITY_TYPE.ORDER_ITEM:
    case TICKET_ENTITY_TYPE.ORDER:
    case TICKET_ENTITY_TYPE.TRANSACTION:
      return `/apps/financial/transactions/view/${id}`;
    case TICKET_ENTITY_TYPE.BUSINESS:
      return `/apps/business/businesses/view/${id}`;
    case TICKET_ENTITY_TYPE.PRODUCT:
      return `/apps/operations/product/view/${id}`;
    case TICKET_ENTITY_TYPE.BOOKING:
      return `/apps/operations/booking/view/${id}`;
    case TICKET_ENTITY_TYPE.REFUND:
      return `/apps/financial/refunds/view/${id}`;
    case TICKET_ENTITY_TYPE.RETURN:
      return `/apps/financial/returns/view/${id}`;
    case TICKET_ENTITY_TYPE.USER:
      return `/apps/user/client-users/view/${id}`;
    default:
      return null;
  }
};

export const formatTicketUserName = (
  user?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
  } | null,
  fallback: string | number | null | undefined = null,
) => {
  if (!user) {
    return fallback == null || fallback === "" ? null : String(fallback);
  }

  const name = `${user.firstName || ""} ${user.lastName || ""}`.trim();
  return name || user.email || (fallback == null ? null : String(fallback));
};

export const hasTicketDisplayId = (value?: string | number | null) => {
  if (value == null || value === "") {
    return false;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0;
};

/** @deprecated use TICKET_STATUS_BADGE */
export const ticketStatusObj = TICKET_STATUS_BADGE;
